import { Bridge, BridgeType } from "../types/bridge";

// Bridge size ranges in tons - EXACTLY as per Traveller ruleset
export const BRIDGE_SIZE_RANGES = [
  { maxSize: 50, bridgeSize: 3 },
  { maxSize: 99, bridgeSize: 6 },
  { maxSize: 200, bridgeSize: 10 },
  { maxSize: 1000, bridgeSize: 20 },
  { maxSize: 2000, bridgeSize: 40 },
  { maxSize: 100000, bridgeSize: 60 },
  { maxSize: Infinity, bridgeSize: 80 }, // Base size for 100,001+ tons
] as const;

// Bridge types and specifications - Updated to match Traveller ruleset
export const BRIDGE_TYPES: Record<BridgeType, Bridge> = {
  STANDARD: {
    type: "STANDARD",
    description: "Standard bridge with all necessary controls and equipment",
    cost: 0, // Cost calculated dynamically based on ship size (MCr0.5 per 100 tons)
    weight: 0, // Weight calculated dynamically based on ship size
    techLevel: 7,
    special: null,
    maintenanceCost: 0, // Calculated as percentage of cost
    power: 2,
    sizeRange: {
      min: 0,
      max: Infinity,
    },
    id: "standard",
  },
  SMALLER: {
    type: "SMALLER",
    description: "Smaller bridge than standard",
    cost: 0, // Half the cost of standard bridge
    weight: 0, // One size smaller than standard
    techLevel: 7,
    special: "DM-1 to spacecraft operations checks",
    maintenanceCost: 0,
    power: 1,
    sizeRange: {
      min: 0,
      max: Infinity,
    },
    id: "smaller",
  },
  COMMAND: {
    type: "COMMAND",
    description: "Command bridge for fleet operations",
    cost: 30000000, // MCr30 additional cost
    weight: 40, // Additional 40 tons
    techLevel: 8,
    special: "+1 to Tactics (naval) checks",
    maintenanceCost: 0,
    power: 3,
    sizeRange: {
      min: 5000, // Only available for ships over 5,000 tons
      max: Infinity,
    },
    id: "command",
  },
  COCKPIT: {
    type: "COCKPIT",
    description: "Single-seat cockpit for ships 50 tons or less",
    cost: 10000, // Cr10,000
    weight: 1.5, // 1.5 tons
    techLevel: 7,
    special: "24h life support, no airlock included",
    maintenanceCost: 100,
    power: 1,
    sizeRange: {
      min: 0,
      max: 50,
    },
    id: "cockpit",
  },
  DUAL_COCKPIT: {
    type: "DUAL_COCKPIT",
    description: "Dual-seat cockpit for ships 50 tons or less",
    cost: 15000, // Cr15,000
    weight: 2.5, // 2.5 tons
    techLevel: 7,
    special: "Two crew positions, 24h life support, no airlock included",
    maintenanceCost: 150,
    power: 1,
    sizeRange: {
      min: 0,
      max: 50,
    },
    id: "dual-cockpit",
  },
} as const;

// Helper functions
export const getBridgeSpec = (type: BridgeType): Bridge => {
  return BRIDGE_TYPES[type];
};

/**
 * Calculate the standard bridge size for a given ship size
 * @param shipSize - Ship size in tons
 * @returns Bridge size in tons
 */
export const calculateStandardBridgeSize = (shipSize: number): number => {
  // Find the appropriate bridge size range
  const range = BRIDGE_SIZE_RANGES.find((r) => shipSize <= r.maxSize);
  if (!range) {
    // For ships over 100,000 tons, add 20 tons for every additional 100,000 tons
    const baseSize = 60; // Base size for 100,001+ tons
    const additionalHundredThousands = Math.floor((shipSize - 100000) / 100000);
    return baseSize + additionalHundredThousands * 20;
  }
  return range.bridgeSize;
};

/**
 * Calculate bridge cost based on Traveller ruleset (MCr0.5 per 100 tons)
 * @param shipSize - Ship size in tons
 * @returns Cost in credits
 */
export const calculateStandardBridgeCost = (shipSize: number): number => {
  const hundredsOfTons = Math.ceil(shipSize / 100);
  return hundredsOfTons * 500000; // MCr0.5 = 500,000 credits
};

/**
 * Calculate bridge cost for a specific bridge type
 * @param bridgeType - Type of bridge
 * @param shipSize - Ship size in tons
 * @returns Cost in credits
 */
export const calculateBridgeCost = (
  bridgeType: BridgeType,
  shipSize: number,
): number => {
  const standardCost = calculateStandardBridgeCost(shipSize);

  switch (bridgeType) {
    case "STANDARD":
      return standardCost;
    case "SMALLER":
      return Math.floor(standardCost / 2); // Half cost
    case "COMMAND":
      return standardCost + 30000000; // Standard cost + MCr30
    case "COCKPIT":
      return 10000; // Fixed Cr10,000
    case "DUAL_COCKPIT":
      return 15000; // Fixed Cr15,000
    default:
      return standardCost;
  }
};

/**
 * Calculate bridge weight for a specific bridge type
 * @param bridgeType - Type of bridge
 * @param shipSize - Ship size in tons
 * @returns Weight in tons
 */
export const calculateBridgeWeight = (
  bridgeType: BridgeType,
  shipSize: number,
): number => {
  const standardSize = calculateStandardBridgeSize(shipSize);

  switch (bridgeType) {
    case "STANDARD":
      return standardSize;
    case "SMALLER":
      // Find the next smaller bridge size
      const smallerRanges = BRIDGE_SIZE_RANGES.filter(
        (r) => r.bridgeSize < standardSize,
      );
      if (smallerRanges.length > 0) {
        return smallerRanges[smallerRanges.length - 1].bridgeSize;
      }
      return Math.floor(standardSize / 2); // Fallback
    case "COMMAND":
      return standardSize + 40; // Standard size + 40 tons
    case "COCKPIT":
      return 1.5;
    case "DUAL_COCKPIT":
      return 2.5;
    default:
      return standardSize;
  }
};

export const calculateBridgeTons = (
  type: BridgeType,
  shipSize: number,
): number => {
  return calculateBridgeWeight(type, shipSize);
};

export const validateBridgeSpec = (spec: Bridge): string[] => {
  const errors: string[] = [];

  if (!spec.type) {
    errors.push("Bridge type is required");
  }

  if (!spec.description) {
    errors.push("Bridge description is required");
  }

  if (spec.weight < 0) {
    errors.push("Weight cannot be negative");
  }

  if (spec.cost < 0) {
    errors.push("Cost cannot be negative");
  }

  if (spec.power < 0) {
    errors.push("Power requirement cannot be negative");
  }

  if (spec.techLevel < 0) {
    errors.push("Tech level cannot be negative");
  }

  if (spec.maintenanceCost < 0) {
    errors.push("Maintenance cost cannot be negative");
  }

  if (spec.sizeRange.min < 0) {
    errors.push("Minimum size cannot be negative");
  }

  if (spec.sizeRange.max <= spec.sizeRange.min) {
    errors.push("Maximum size must be greater than minimum size");
  }

  return errors;
};

export const validateBridgeSize = (
  spec: Bridge,
  shipTons: number,
): string[] => {
  const errors: string[] = [];

  if (shipTons < spec.sizeRange.min) {
    errors.push(
      `Ship is too small for ${spec.type} bridge (minimum ${spec.sizeRange.min} tons)`,
    );
  }

  if (shipTons > spec.sizeRange.max) {
    errors.push(
      `Ship is too large for ${spec.type} bridge (maximum ${spec.sizeRange.max} tons)`,
    );
  }

  return errors;
};
